import { Button, FormControl, FormErrorMessage, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FiTool } from 'react-icons/fi';
import { Select, CreatableSelect } from 'chakra-react-select';
import { chakraStyles } from '../common/select/styles';
import { useGetEquipmentCategoriesQuery, useAddEquipmentCategoryMutation, useAddEquipmentMutation, useDeleteEquipmentCategoryMutation, useModalitiesQuery } from '@/app/services/nucleus';
import LoaderOverlay from '../common/LoaderOverlay';
import AlertComponent from '../common/AlertComponent';
import FormRow from '@/features/user/FormRow';
import { OptionCategoryDelete } from '@/features/common/select/Option';

const EquipmentModal = ({ isOpen, onClose, onSave }) => {
  const initialRef = useRef(null);
  const finalRef = useRef(null);

  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState(null);
  const [modalities, setModalities] = useState([]);
  const [nameError, setNameError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [deleteErrors, setDeleteErrors] = useState({});

  const { data: categoriesData } = useGetEquipmentCategoriesQuery();
  const { data: modalitiesData } = useModalitiesQuery();

  const [addEquipmentCategory, { isLoading: isCreatingCategory }] = useAddEquipmentCategoryMutation();
  const [addEquipment, { isLoading: isSubmitting, isError, error, reset }] = useAddEquipmentMutation();
  const [deleteEquipmentCategory] = useDeleteEquipmentCategoryMutation();

  const modalityOptions = useMemo(() => {
    return (modalitiesData?.items || []).map((m) => ({ value: m.id, label: m.short_name }));
  }, [modalitiesData?.items]);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setBrand('');
      setCategory(null);
      setModalities([]);
      setNameError(false);
      setCategoryError(false);
      setPendingDeleteId(null);
      setDeletingIds(new Set());
      setDeleteErrors({});
      reset();
    }
  }, [isOpen]);

  const handleCreateCategory = async (inputValue) => {
    try {
      const result = await addEquipmentCategory({ name: inputValue }).unwrap();
      const newOption = { value: result.id, label: result.name };
      setCategory(newOption);
      setCategoryError(false);
    } catch (e) {
      console.error('Error creating category', e);
    }
  };

  const handleRequestDelete = (id) => setPendingDeleteId(id);

  const handleCancelDelete = () => setPendingDeleteId(null);

  const handleConfirmDelete = async (id) => {
    setPendingDeleteId(null);
    setDeletingIds((prev) => new Set([...prev, id]));
    try {
      await deleteEquipmentCategory(id).unwrap();
      if (category?.value === id) setCategory(null);
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      setDeleteErrors((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } catch (e) {
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      setDeleteErrors((prev) => ({ ...prev, [id]: e?.data?.detail ?? 'Could not delete' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasError = false;
    if (!name.trim()) { setNameError(true); hasError = true; }
    if (!category) { setCategoryError(true); hasError = true; }
    if (hasError) return;

    addEquipment({
      name: name.trim(),
      brand: brand.trim(),
      category_id: category.value,
      modality_ids: modalities.map((m) => m.value),
    })
      .unwrap()
      .then(() => { onSave(); onClose(); })
      .catch((e) => console.error('Error adding equipment', e));
  };

  return (
    <Modal
      initialFocusRef={initialRef}
      finalFocusRef={finalRef}
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="2xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <form id="add_equipment" onSubmit={handleSubmit}>
          <ModalHeader>
            <HStack><FiTool /><Text>Add Equipment</Text></HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} position="relative">
              <LoaderOverlay loading={isSubmitting || isCreatingCategory} />
              {isError && (
                <AlertComponent status="error" title="Error adding equipment" description={error?.data?.detail ?? 'An error occurred'} />
              )}
              <FormControl isInvalid={nameError} aria-label="name-control">
                <FormRow id="name" label="Name" templateColumns="110px auto" gap={0} width="100%">
                  <Input ref={initialRef} id="name" focusBorderColor="brand.700" value={name} onChange={(e) => { setName(e.target.value); setNameError(false); }} />
                </FormRow>
                <FormErrorMessage ml="110px">Name is required.</FormErrorMessage>
              </FormControl>
              <FormControl aria-label="brand-control">
                <FormRow id="brand" label="Brand" templateColumns="110px auto" gap={0} width="100%">
                  <Input id="brand" focusBorderColor="brand.700" value={brand} onChange={(e) => setBrand(e.target.value)} />
                </FormRow>
              </FormControl>
              <FormControl isInvalid={categoryError} aria-label="category-control">
                <FormRow id="category" label="Category" templateColumns="110px auto" gap={0} width="100%">
                  <CreatableSelect
                    id="category"
                    aria-label="category"
                    chakraStyles={chakraStyles}
                    focusBorderColor="brand.700"
                    options={categoriesData || []}
                    value={category}
                    onChange={(opt) => { setCategory(opt); setCategoryError(false); }}
                    onCreateOption={handleCreateCategory}
                    onMenuClose={handleCancelDelete}
                    isClearable
                    isSearchable
                    placeholder="Select or create a category"
                    components={{ Option: OptionCategoryDelete }}
                    onRequestDelete={handleRequestDelete}
                    onConfirmDelete={handleConfirmDelete}
                    onCancelDelete={handleCancelDelete}
                    pendingDeleteId={pendingDeleteId}
                    deletingIds={deletingIds}
                    deleteErrors={deleteErrors}
                  />
                </FormRow>
                <FormErrorMessage ml="110px">Category is required.</FormErrorMessage>
              </FormControl>
              <FormControl aria-label="modalities-control">
                <FormRow id="modalities" label="Modalities" templateColumns="110px auto" gap={0} width="100%">
                  <Select
                    id="modalities"
                    aria-label="modalities"
                    chakraStyles={chakraStyles}
                    focusBorderColor="brand.700"
                    options={modalityOptions}
                    value={modalities}
                    onChange={(opts) => setModalities(opts || [])}
                    isMulti
                    isClearable
                    isSearchable
                    colorScheme="blue"
                    placeholder="Select modalities"
                  />
                </FormRow>
              </FormControl>
              <HStack width="100%" py={3} justifyContent="end">
                <Button onClick={onClose}>Cancel</Button>
                <Button type="submit" variant="purple">Submit</Button>
              </HStack>
            </VStack>
          </ModalBody>
        </form>
      </ModalContent>
    </Modal>
  );
};

EquipmentModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

export default EquipmentModal;
